import React, { useState, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import axios from "axios"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  InputGroup,
  InputGroupText,
  Input,
  Spinner,
  Form,
  FormGroup,
  Label,
  Badge,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Table,
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  UncontrolledTooltip,
} from "reactstrap"
import TableContainer from "../../components/Common/TableContainer"
import classnames from "classnames"
import apiURl, { baseURl } from "url"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faEye,
  faFilter,
  faSearch,
  faPlus,
  faCalendar,
  faMapMarkerAlt,
  faPhone,
  faEnvelope,
  faEyeSlash,
  faFileAlt,
  faBuilding,
  faUniversity,
  faMapPin,
  faGlobe,
  faTruck,
  faClock,
  faCheck,
  faTimes,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons"
import BusinessRegistrationModal from "./RegisterNewBusiness" // Adjust path as needed
import ExcelDownload from "components/Common/ExcelDownload"

const BusinessManagement = () => {
  document.title = "ECS Business Management"
  const navigate = useNavigate()

  // Authorization check
  const role_ = localStorage.getItem("role")
  if (role_ !== "admin") {
    navigate("/")
  }

  // State management
  const [businesses, setBusinesses] = useState([])
  const [modal, setModal] = useState(false)
  const [selectedBusiness, setSelectedBusiness] = useState(null)
  const [activeTab, setActiveTab] = useState("2")
  const [isLoading, setIsLoading] = useState(false)

  // Rejection modal states
  const [rejectionModal, setRejectionModal] = useState(false)
  const [pendingBusinessId, setPendingBusinessId] = useState(null)
  const [rejectionReason, setRejectionReason] = useState("")

  // New Business modal
  const [businessModal, setBusinessModal] = useState(false)

  // Filters state
  const [filterOpen, setFilterOpen] = useState(false)
  const toggleFilter = () => setFilterOpen(!filterOpen)
  const [filters, setFilters] = useState({
    search: "",
    city: "",
    dateFrom: "",
    dateTo: "",
  })

  // Cities list (derived from data or static; can be dynamic)
  const availableCities = useMemo(() => {
    const cities = [
      ...new Set(businesses.map(b => b.businessCity).filter(Boolean)),
    ]
    return cities.sort()
  }, [businesses])

  // Counts for tabs
  const newRequestsCount = useMemo(
    () =>
      businesses.filter(
        b => b.accountStatus === "requested" || b.accountStatus === "Pending"
      ).length,
    [businesses]
  )
  const rejectedCount = useMemo(
    () => businesses.filter(b => b.accountStatus === "Rejected").length,
    [businesses]
  )

  const handleNewBusiness = () => {
    setBusinessModal(true)
  }

  const handleCloseModal = () => {
    setBusinessModal(false)
  }

  // Filter businesses based on current filters and tab
  const filteredBusinesses = useMemo(() => {
    let filtered = businesses.filter(business => {
      // Status filter based on active tab
      if (activeTab === "2") {
        return (
          business.accountStatus === "requested" ||
          business.accountStatus === "Pending"
        )
      } else if (activeTab === "3") {
        return business.accountStatus === "Rejected"
      }
      return true // For approved if uncommented
    })

    // Apply search filters
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(business => {
        const businessName = (business.businessName || "").toLowerCase()
        const accountMail = (business.accountMail || "").toLowerCase()
        const businessPhone = business.businessPhone || ""
        return (
          businessName.includes(searchLower) ||
          accountMail.includes(searchLower) ||
          businessPhone.includes(searchLower)
        )
      })
    }

    // City filter
    if (filters.city) {
      const cityLower = filters.city.toLowerCase()
      filtered = filtered.filter(business =>
        (business.businessCity || "").toLowerCase().includes(cityLower)
      )
    }

    // Date range filter
    if (filters.dateFrom || filters.dateTo) {
      const fromDate = filters.dateFrom
        ? new Date(filters.dateFrom)
        : new Date(0)
      const toDate = filters.dateTo ? new Date(filters.dateTo) : new Date()
      toDate.setHours(23, 59, 59, 999)
      filtered = filtered.filter(business => {
        const createdDate = new Date(business.createdAt)
        return createdDate >= fromDate && createdDate <= toDate
      })
    }

    return filtered
  }, [businesses, activeTab, filters])

  // Fetch businesses data
  const getAllBusinesses = async () => {
    try {
      setIsLoading(true)
      const URL = `${apiURl}/getAllBusinesses`
      const response = await axios.get(URL)

      if (response.data.status === "success") {
        setBusinesses(response.data.businesses)
      } else {
        console.error("Failed to fetch businesses:", response.data.message)
      }
    } catch (error) {
      console.error("Error fetching businesses:", error)
    } finally {
      setIsLoading(false)
    }
  }

  // Document tabs
  const [activeDocTab, setActiveDocTab] = useState("cnicFront")

  const toggleDocTab = tab => {
    if (activeDocTab !== tab) setActiveDocTab(tab)
  }

  const documents = useMemo(
    () => [
      {
        key: "cnicFront",
        label: "CNIC Front",
        url: selectedBusiness?.frontCnicImage,
      },
      {
        key: "cnicBack",
        label: "CNIC Back",
        url: selectedBusiness?.backCnicImage,
      },
      {
        key: "ntn",
        label: "NTN License",
        url: selectedBusiness?.ntnImage,
      },
      {
        key: "blankCheck",
        label: "Blank Cheque",
        url: selectedBusiness?.blankChequeImage,
      },
      {
        key: "shipperPic",
        label: "Shipper Picture",
        url: selectedBusiness?.shipperPicImage,
      },
      {
        key: "shopFascia",
        label: "Shop Fascia",
        url: selectedBusiness?.shopFasciaImage,
      },
    ],
    [selectedBusiness]
  )

  // Update business status
  const updateBusinessStatus = async (businessId, newStatus, reason = null) => {
    try {
      const URL = `${apiURl}/updateBusinessStatus`
      const payload = {
        id: businessId,
        accountStatus: newStatus,
      }
      if (reason !== null) {
        payload.rejectionReason = reason
      } else if (newStatus !== "Rejected") {
        payload.rejectionReason = ""
      }
      const response = await axios.put(URL, payload)

      if (response.data.status === "success") {
        toastr.success("Status updated successfully")
        getAllBusinesses()
        return true
      } else {
        console.error("Failed to update status:", response.data.message)
        return false
      }
    } catch (error) {
      console.error("Error updating status:", error)
      return false
    }
  }

  // Initialize component
  useEffect(() => {
    getAllBusinesses()
  }, [])

  // Toggle modal and set selected business
  const toggleModal = (business = null) => {
    setSelectedBusiness(business)
    setModal(!modal)
  }

  // Toggle between tabs
  const toggleTab = tab => {
    if (activeTab !== tab) setActiveTab(tab)
  }

  // Reset filters
  const resetFilters = () => {
    setFilters({
      search: "",
      city: "",
      dateFrom: "",
      dateTo: "",
    })
  }

  // Handle approve
  const handleApprove = async businessId => {
    const success = await updateBusinessStatus(businessId, "Approved")
    if (success) {
      setModal(false)
      setSelectedBusiness(null)
    }
  }

  // Handle reject open
  const handleRejectOpen = businessId => {
    setPendingBusinessId(businessId)
    setRejectionReason("")
    setRejectionModal(true)
  }

  // Hash management for tabs
  const HASH_FOR_TAB = {
    2: "#newBusiness",
    3: "#rejectedBusiness",
  }
  const TAB_FOR_HASH = Object.fromEntries(
    Object.entries(HASH_FOR_TAB).map(([k, v]) => [v, k])
  )

  const setHashForTab = tab => {
    const { pathname, search } = window.location
    const hash = HASH_FOR_TAB[tab] || ""
    window.history.replaceState(null, "", `${pathname}${search}${hash}`)
  }

  useEffect(() => {
    const apply = () => {
      const t = TAB_FOR_HASH[window.location.hash]
      if (t && t !== activeTab) toggleTab(t)
    }
    apply()
    window.addEventListener("hashchange", apply)
    return () => window.removeEventListener("hashchange", apply)
  }, [activeTab])

  // Common columns for tables
  const baseColumns = [
    {
      Header: "ID",
      accessor: "id",
      disableFilters: true,
      Cell: ({ value }) => (
        <span className="fw-semibold">
          {value?.toString().trim() || "----"}
        </span>
      ),
    },
    {
      Header: "Business Name",
      accessor: "businessName",
      disableFilters: true,
      Cell: ({ value }) => (
        <span className="fw-medium">{value?.toString().trim() || "----"}</span>
      ),
    },
    {
      Header: "Email",
      accessor: "accountMail",
      disableFilters: true,
      Cell: ({ value }) => <span>{value?.toString().trim() || "----"}</span>,
    },
    {
      Header: "Phone",
      accessor: "businessPhone",
      disableFilters: true,
      Cell: ({ value }) => <span>{value?.toString().trim() || "----"}</span>,
    },
    {
      Header: "NTN",
      accessor: "ntn",
      disableFilters: true,
      Cell: ({ value }) => (
        <span className="fw-semibold">
          {(value || "").toString().trim() || "---"}
        </span>
      ),
    },
    {
      Header: "City",
      accessor: "businessCity",
      disableFilters: true,
      Cell: ({ value }) => (
        <Badge color="info" pill>
          <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
          {value?.toString().trim() || "----"}
        </Badge>
      ),
    },
    {
      Header: "Created Date",
      accessor: "createdAt",
      disableFilters: true,
      Cell: ({ value }) => (
        <span>
          <FontAwesomeIcon icon={faCalendar} className="me-1 text-muted" />
          {value?.toString().trim() || "----"}
        </span>
      ),
    },
  ]

  // Columns for New Requests / Rejected (show only badge)
  const requestColumns = useMemo(
    () => [
      ...baseColumns,
      {
        Header: "Status",
        accessor: "accountStatus",
        disableFilters: true,
        Cell: ({ value }) => (
          <Badge
            color={
              value === "Approved"
                ? "success"
                : value === "Rejected"
                ? "danger"
                : "warning"
            }
            pill
          >
            {value || "----"}
          </Badge>
        ),
      },
      {
        Header: "Actions",
        accessor: "actions",
        disableFilters: true,
        Cell: ({ row }) => {
          const tooltipId = `view-${row.id}` // unique per row

          return (
            <>
              <FontAwesomeIcon
                id={tooltipId}
                icon={faEye}
                className="me-1"
                style={{ cursor: "pointer" }}
                onClick={() => toggleModal(row.original)}
              />
              <UncontrolledTooltip placement="right" target={tooltipId}>
                Detail
              </UncontrolledTooltip>
            </>
          )
        },
      },
    ],
    []
  )

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row className="align-items-center mb-4">
            <Col xl="6">
              <div className="d-flex align-items-center">
                <h4 className="mb-0 fw-semibold text-dark">
                  New Business Requests
                </h4>
                <Badge color="info" className="ms-2">
                  {filteredBusinesses.length} Results
                </Badge>
              </div>
            </Col>
            <Col xl="6" className="text-end">
              <Button
                color="success"
                className="btn-sm px-4"
                onClick={handleNewBusiness}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add New Business
              </Button>
            </Col>
          </Row>

          {/* Filters Row */}
          <Accordion
            open={filterOpen ? "1" : ""}
            toggle={toggleFilter}
            className="mb-4"
          >
            <AccordionItem>
              <AccordionHeader
                targetId="1"
                className="fw-semibold bg-light rounded-3"
              >
                <i className="bx bx-filter me-2"></i> Filters
              </AccordionHeader>

              <AccordionBody accordionId="1">
                <div className="p-3 bg-light rounded-3">
                  <Row className="g-3 align-items-end">
                    {/* Search */}
                    <Col md={3}>
                      <Label className="fw-semibold">Search</Label>
                      <InputGroup className="input-group-sm">
                        <InputGroupText>
                          <FontAwesomeIcon
                            icon={faSearch}
                            className="text-muted"
                          />
                        </InputGroupText>
                        <Input
                          type="text"
                          placeholder="Name, email, phone, or NTN..."
                          value={filters.search}
                          onChange={e =>
                            setFilters({
                              ...filters,
                              search: e.target.value,
                            })
                          }
                        />
                      </InputGroup>
                    </Col>

                    {/* City */}
                    <Col md={2}>
                      <Label className="fw-semibold">City</Label>
                      <InputGroup
                        className="input-group-sm"
                        style={{ maxWidth: "200px" }}
                      >
                        <InputGroupText addonType="prepend">
                          <FontAwesomeIcon
                            icon={faMapMarkerAlt}
                            className="text-muted"
                          />
                        </InputGroupText>
                        <Input
                          type="text"
                          placeholder="Filter by city..."
                          value={filters.city}
                          onChange={e =>
                            setFilters({
                              ...filters,
                              city: e.target.value,
                            })
                          }
                          className="form-control-sm"
                        />
                      </InputGroup>
                    </Col>

                    {/* Date From */}
                    <Col md={2}>
                      <Label className="fw-semibold">From</Label>
                      <InputGroup className="input-group-sm">
                        <InputGroupText>
                          <FontAwesomeIcon
                            icon={faCalendar}
                            className="text-muted"
                          />
                        </InputGroupText>
                        <Input
                          type="date"
                          value={filters.dateFrom}
                          onChange={e =>
                            setFilters({
                              ...filters,
                              dateFrom: e.target.value,
                            })
                          }
                          max={
                            filters.dateTo ||
                            new Date().toISOString().split("T")[0]
                          }
                        />
                      </InputGroup>
                    </Col>

                    {/* Date To */}
                    <Col md={2}>
                      <Label className="fw-semibold">To</Label>
                      <Input
                        type="date"
                        value={filters.dateTo}
                        onChange={e =>
                          setFilters({
                            ...filters,
                            dateTo: e.target.value,
                          })
                        }
                        min={filters.dateFrom}
                        className="form-control form-control-sm"
                      />
                    </Col>

                    {/* Actions */}
                    <Col md={1} className="text-end">
                      <Button
                        color="secondary"
                        size="sm"
                        onClick={resetFilters}
                        className="mt-2"
                      >
                        <i className="bx bx-refresh me-1"></i>
                        Clear
                      </Button>
                    </Col>
                  </Row>
                </div>
              </AccordionBody>
            </AccordionItem>
          </Accordion>

          <Row>
            <Col lg="12">
              <Card className="shadow-sm border-0">
                <CardBody>
                  {isLoading ? (
                    <div className="d-flex justify-content-center align-items-center py-5">
                      <Spinner color="primary" className="me-2" />
                      <span>Loading businesses...</span>
                    </div>
                  ) : (
                    <>
                      {/* Enhanced Tabs with Icons and Counts */}
                      <Nav
                        tabs
                        className="nav-tabs-custom flex-wrap gap-2 mb-4"
                      >
                        <NavItem>
                          <NavLink
                            className={classnames({
                              active: activeTab === "2",
                            })}
                            onClick={() => {
                              toggleTab("2")
                              setHashForTab("2")
                            }}
                            style={{
                              backgroundColor:
                                activeTab === "2" ? "#50A5F1" : "#f8f9fa",
                              color: activeTab === "2" ? "white" : "#6c757d",
                              border: "1px solid #dee2e6",
                              borderRadius: "8px 8px 0 0",
                              transition: "0.3s ease",
                              padding: "12px 20px",
                            }}
                          >
                            <FontAwesomeIcon icon={faPlus} className="me-1" />{" "}
                            New Requests ({newRequestsCount})
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            className={classnames({
                              active: activeTab === "3",
                            })}
                            onClick={() => {
                              toggleTab("3")
                              setHashForTab("3")
                            }}
                            style={{
                              backgroundColor:
                                activeTab === "3" ? "#F46A6A" : "#f8f9fa",
                              color: activeTab === "3" ? "white" : "#6c757d",
                              border: "1px solid #dee2e6",
                              borderRadius: "8px 8px 0 0",
                              transition: "0.3s ease",
                              padding: "12px 20px",
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faEyeSlash}
                              className="me-1"
                            />{" "}
                            Rejected ({rejectedCount})
                          </NavLink>
                        </NavItem>
                      </Nav>

                      {/* Tab Content */}
                      <TabContent activeTab={activeTab}>
                        <TabPane tabId="2">
                          <div className="table-responsive">
                            <div className="float-end d-flex gap-2">
                              <div className="mt-3">
                                <ExcelDownload
                                  leads={filteredBusinesses}
                                  title="Export New Business List"
                                />
                              </div>
                            </div>
                            <TableContainer
                              columns={requestColumns}
                              data={filteredBusinesses}
                              isGlobalFilter={true} // Disabled since we have custom filters
                              customPageSize={10}
                              className="custom-table"
                              divClass="table-responsive"
                            />
                          </div>
                        </TabPane>
                        <TabPane tabId="3">
                          <div className="table-responsive">
                            <div className="float-end d-flex gap-2">
                              <div className="mt-3">
                                <ExcelDownload
                                  leads={filteredBusinesses}
                                  title="Export Rejected Business List"
                                />
                              </div>
                            </div>
                            <TableContainer
                              columns={requestColumns}
                              data={filteredBusinesses}
                              isGlobalFilter={true}
                              customPageSize={10}
                              className="custom-table"
                              divClass="table-responsive"
                            />
                          </div>
                        </TabPane>
                      </TabContent>

                      {filteredBusinesses.length === 0 && (
                        <div className="text-center py-5">
                          <FontAwesomeIcon
                            icon={faSearch}
                            size="3x"
                            className="text-muted mb-3"
                          />
                          <h5 className="text-muted">No businesses found</h5>
                          <p className="text-muted">
                            Try adjusting your filters or check back later.
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Enhanced Business Details Modal */}
        <Modal
          isOpen={modal}
          toggle={toggleModal}
          centered
          size="xl"
          scrollable
          style={{ maxHeight: "90vh" }}
        >
          <ModalHeader toggle={toggleModal} className="bg-primary text-white">
            <FontAwesomeIcon icon={faEye} className="me-2" />
            Business Details - {selectedBusiness?.businessName}
          </ModalHeader>
          <ModalBody
            className="p-0"
            style={{ maxHeight: "70vh", overflowY: "auto" }}
          >
            {selectedBusiness && (
              <div>
                <Row className="g-3 p-4">
                  <Col md="6">
                    <Card className="border-0 h-100">
                      <CardBody className="p-3">
                        <h6 className="text-primary mb-3">
                          <FontAwesomeIcon icon={faEnvelope} className="me-1" />{" "}
                          Contact Information
                        </h6>
                        <dl className="row mb-0">
                          <dt className="col-sm-4 fw-semibold text-muted small">
                            Email:
                          </dt>
                          <dd className="col-sm-8 mb-2 small">
                            {selectedBusiness.accountMail || "----"}
                          </dd>
                          <dt className="col-sm-4 fw-semibold text-muted small">
                            Alt. Email:
                          </dt>
                          <dd className="col-sm-8 mb-2 small">
                            {selectedBusiness.email || "----"}
                          </dd>
                          <dt className="col-sm-4 fw-semibold text-muted small">
                            Phone:
                          </dt>
                          <dd className="col-sm-8 mb-2 small">
                            {selectedBusiness.businessPhone || "----"}
                          </dd>
                          <dt className="col-sm-4 fw-semibold text-muted small">
                            Alt. Phone:
                          </dt>
                          <dd className="col-sm-8 mb-2 small">
                            {selectedBusiness.phone2 || "----"}
                          </dd>
                          <dt className="col-sm-4 fw-semibold text-muted small">
                            POC Name:
                          </dt>
                          <dd className="col-sm-8 mb-2 small fw-medium">
                            {selectedBusiness.pocName || "----"}
                          </dd>
                          <dt className="col-sm-4 fw-semibold text-muted small">
                            CNIC:
                          </dt>
                          <dd className="col-sm-8 small">
                            {selectedBusiness.cnic || "----"}
                          </dd>
                        </dl>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="6">
                    <Card className="border-0 h-100">
                      <CardBody className="p-3">
                        <h6 className="text-primary mb-3">
                          <FontAwesomeIcon
                            icon={faMapMarkerAlt}
                            className="me-1"
                          />{" "}
                          Business Details
                        </h6>
                        <dl className="row mb-0">
                          <dt className="col-sm-4 fw-semibold text-muted small">
                            NTN:
                          </dt>
                          <dd className="col-sm-8 mb-2 small fw-semibold">
                            {selectedBusiness.ntn || "----"}
                          </dd>
                          <dt className="col-sm-4 fw-semibold text-muted small">
                            STRN:
                          </dt>
                          <dd className="col-sm-8 mb-2 small">
                            {selectedBusiness.strn || "----"}
                          </dd>
                          <dt className="col-sm-4 fw-semibold text-muted small">
                            Address:
                          </dt>
                          <dd className="col-sm-8 mb-2 small">
                            {selectedBusiness.businessAddress || "----"}
                          </dd>
                          <dt className="col-sm-4 fw-semibold text-muted small">
                            City:
                          </dt>
                          <dd className="col-sm-8 mb-2 small">
                            <Badge color="info" className="me-1">
                              {selectedBusiness.businessCity || "----"}
                            </Badge>
                          </dd>
                          <dt className="col-sm-4 fw-semibold text-muted small">
                            Nature:
                          </dt>
                          <dd className="col-sm-8 mb-2 small">
                            <Badge
                              color={
                                selectedBusiness.nature === "Non-COD"
                                  ? "warning"
                                  : "info"
                              }
                              className="me-1"
                            >
                              {selectedBusiness.nature || "----"}
                            </Badge>
                            {selectedBusiness.nature === "Non-COD" &&
                              selectedBusiness.nonCodType && (
                                <small className="text-muted">
                                  ({selectedBusiness.nonCodType})
                                </small>
                              )}
                          </dd>
                          <dt className="col-sm-4 fw-semibold text-muted small">
                            Created:
                          </dt>
                          <dd className="col-sm-8 mb-2 small">
                            {selectedBusiness.createdAt || "----"}
                          </dd>
                          <dt className="col-sm-4 fw-semibold text-muted small">
                            Expected Shipments:
                          </dt>
                          <dd className="col-sm-8 mb-2 small">
                            {selectedBusiness.expectedShipments || "----"}
                          </dd>
                          <dt className="col-sm-4 fw-semibold text-muted small">
                            Product Type:
                          </dt>
                          <dd className="col-sm-8 mb-2 small">
                            {selectedBusiness.productType || "----"}
                          </dd>
                          <dt className="col-sm-4 fw-semibold text-muted small">
                            Sales Person:
                          </dt>
                          <dd className="col-sm-8 mb-2 small">
                            {selectedBusiness.salesPerson || "----"}
                          </dd>
                          <dt className="col-sm-4 fw-semibold text-muted small">
                            Website:
                          </dt>
                          <dd className="col-sm-8 mb-2 small">
                            <a
                              href={selectedBusiness.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-decoration-none small"
                            >
                              <FontAwesomeIcon
                                icon={faGlobe}
                                className="me-1"
                              />
                              {selectedBusiness.url
                                ? selectedBusiness.url.substring(0, 30) + "..."
                                : "----"}
                            </a>
                          </dd>
                        </dl>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Banks Section */}
                {selectedBusiness.banks &&
                  selectedBusiness.banks.length > 0 && (
                    <Row className="mt-1">
                      <Col md="12">
                        <Card className="border-0">
                          <CardBody className="p-3">
                            <h6 className="text-primary mb-3">
                              <FontAwesomeIcon
                                icon={faUniversity}
                                className="me-1"
                              />{" "}
                              Bank Details
                            </h6>
                            <div className="table-responsive">
                              <Table className="table table-sm mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th className="small">No.</th>
                                    <th className="small">Bank Name</th>
                                    <th className="small">Branch</th>
                                    <th className="small">City</th>
                                    <th className="small">Account Title</th>
                                    <th className="small">IBAN</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedBusiness.banks.map((bank, index) => (
                                    <tr key={index}>
                                      <td className="small">{index + 1}</td>
                                      <td className="small fw-medium">
                                        {bank.bankName || "----"}
                                      </td>
                                      <td className="small">
                                        {bank.branchName || "----"}
                                      </td>
                                      <td className="small">
                                        {bank.branchCity || "----"}
                                      </td>
                                      <td className="small">
                                        {bank.accountTitle || "----"}
                                      </td>
                                      <td
                                        className="small text-truncate"
                                        style={{ maxWidth: "120px" }}
                                      >
                                        {bank.iban || "----"}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                  )}

                {/* Pickups Section */}
                {selectedBusiness.pickups &&
                  selectedBusiness.pickups.length > 0 && (
                    <Row className="mt-2">
                      <Col md="12">
                        <Card className="border-0">
                          <CardBody className="p-3">
                            <h6 className="text-primary mb-3">
                              <FontAwesomeIcon
                                icon={faTruck}
                                className="me-1"
                              />{" "}
                              Pickup Locations
                            </h6>
                            <div className="table-responsive">
                              <Table className="table table-sm mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th className="small">No.</th>
                                    <th className="small">Brand</th>
                                    <th className="small">Address</th>
                                    <th className="small">City</th>
                                    <th className="small">Phone</th>
                                    <th className="small">Email</th>
                                    <th className="small">POC</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedBusiness.pickups.map(
                                    (pickup, index) => (
                                      <tr key={index}>
                                        <td className="small">{index + 1}</td>
                                        <td className="small">
                                          {selectedBusiness.brandName || "----"}
                                        </td>
                                        <td
                                          className="small text-truncate"
                                          style={{ maxWidth: "150px" }}
                                        >
                                          {pickup.address || "----"}
                                        </td>
                                        <td className="small">
                                          {pickup.city || "----"}
                                        </td>
                                        <td className="small">
                                          {pickup.phone || "----"}
                                        </td>
                                        <td
                                          className="small text-truncate"
                                          style={{ maxWidth: "100px" }}
                                        >
                                          {pickup.email || "----"}
                                        </td>
                                        <td className="small">
                                          {pickup.poc || "----"}
                                        </td>
                                      </tr>
                                    )
                                  )}
                                </tbody>
                              </Table>
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                  )}

                {/* Documents Section */}
                <Row className="mt-2">
                  <Col md="12">
                    <h6 className="text-primary mb-3 px-3">
                      <FontAwesomeIcon icon={faFileAlt} className="me-1" />{" "}
                      Uploaded Documents
                    </h6>
                    <Card className="border-0">
                      <CardBody className="p-0">
                        {/* Document Tabs */}
                        <Nav tabs className="nav-tabs-custom">
                          {documents.map(doc => (
                            <NavItem key={doc.key}>
                              <NavLink
                                className={classnames({
                                  active: activeDocTab === doc.key,
                                })}
                                onClick={() => toggleDocTab(doc.key)}
                                style={{
                                  cursor: "pointer",
                                  padding: "8px 12px",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {doc.label}
                              </NavLink>
                            </NavItem>
                          ))}
                        </Nav>

                        {/* Document Content */}
                        <TabContent activeTab={activeDocTab} className="p-3">
                          {documents.map(doc => (
                            <TabPane key={doc.key} tabId={doc.key}>
                              {doc.url ? (
                                <div className="text-center">
                                  <div
                                    className="d-inline-block border rounded shadow-sm bg-light p-2 mb-2"
                                    style={{
                                      maxWidth: "300px",
                                      maxHeight: "250px",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <img
                                      src={`${baseURl}${doc.url}`}
                                      alt={doc.label}
                                      className="img-fluid rounded"
                                      style={{
                                        maxHeight: "200px",
                                        objectFit: "contain",
                                      }}
                                    />
                                  </div>
                                  <Button
                                    color="outline-primary"
                                    size="sm"
                                    onClick={() =>
                                      window.open(
                                        `${baseURl}${doc.url}`,
                                        "_blank"
                                      )
                                    }
                                    className="px-3"
                                  >
                                    <FontAwesomeIcon
                                      icon={faEye}
                                      className="me-1"
                                    />
                                    View Full
                                  </Button>
                                </div>
                              ) : (
                                <div className="text-center py-3">
                                  <FontAwesomeIcon
                                    icon={faFileAlt}
                                    size="2x"
                                    className="text-muted mb-2"
                                  />
                                  <p className="text-muted small mb-0">
                                    No document for {doc.label.toLowerCase()}.
                                  </p>
                                </div>
                              )}
                            </TabPane>
                          ))}
                        </TabContent>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Status Section - Compact and Professional */}
                {(selectedBusiness.accountStatus === "requested" ||
                  selectedBusiness.accountStatus === "Pending") && (
                  <Row className="g-3 mt-2 p-3 bg-light rounded">
                    <Col md="12">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          <Badge
                            color="warning"
                            pill
                            className="fs-6 px-3 py-2 me-2"
                          >
                            <FontAwesomeIcon icon={faClock} className="me-1" />
                            {selectedBusiness.accountStatus || "Pending"}
                          </Badge>
                          <small className="text-muted">Action Required</small>
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            color="success"
                            size="sm"
                            className="px-4"
                            onClick={() => handleApprove(selectedBusiness.id)}
                          >
                            <FontAwesomeIcon icon={faCheck} className="me-1" />
                            Approve
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            outline
                            className="px-4"
                            onClick={() =>
                              handleRejectOpen(selectedBusiness.id)
                            }
                          >
                            <FontAwesomeIcon icon={faTimes} className="me-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}

                {selectedBusiness.rejectionReason && (
                  <Row className="mt-2">
                    <Col md="12">
                      <Card className="border-0 bg-danger-subtle">
                        <CardBody className="p-3">
                          <h6 className="text-danger mb-2">
                            <FontAwesomeIcon
                              icon={faExclamationTriangle}
                              className="me-1"
                            />
                            Rejection Reason
                          </h6>
                          <p className="mb-0 text-danger small">
                            {selectedBusiness.rejectionReason}
                          </p>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                )}
              </div>
            )}
          </ModalBody>
          <ModalFooter className="p-2">
            <Button color="secondary" size="sm" onClick={toggleModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>

        {/* Rejection Modal */}
        <Modal
          isOpen={rejectionModal}
          toggle={() => {
            setRejectionModal(false)
            setPendingBusinessId(null)
            setRejectionReason("")
          }}
          centered
        >
          <ModalHeader
            toggle={() => {
              setRejectionModal(false)
              setPendingBusinessId(null)
              setRejectionReason("")
            }}
            className="bg-danger text-white"
          >
            Provide Rejection Reason
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="rejectionReason" className="fw-semibold">
                Reason for Rejection <span className="text-danger">*</span>
              </Label>
              <Input
                type="textarea"
                id="rejectionReason"
                name="rejectionReason"
                value={rejectionReason}
                onChange={e => setRejectionReason(e.target.value)}
                placeholder="Enter a clear and concise reason for rejecting this business registration..."
                rows="4"
                className="form-control"
                required
              />
              <small className="text-muted">
                This reason will be visible to the business owner.
              </small>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              size="sm"
              onClick={() => {
                setRejectionModal(false)
                setPendingBusinessId(null)
                setRejectionReason("")
              }}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              size="sm"
              onClick={async () => {
                if (!rejectionReason.trim()) {
                  toastr.error("Please provide a rejection reason.")
                  return
                }
                const success = await updateBusinessStatus(
                  pendingBusinessId,
                  "Rejected",
                  rejectionReason
                )
                if (success) {
                  setModal(false)
                  setSelectedBusiness(null)
                }
                setRejectionModal(false)
                setPendingBusinessId(null)
                setRejectionReason("")
              }}
              disabled={!rejectionReason.trim()}
            >
              Confirm Rejection
            </Button>
          </ModalFooter>
        </Modal>

        <BusinessRegistrationModal
          isOpen={businessModal}
          toggle={handleCloseModal}
          getAllBusinesses={getAllBusinesses}
        />
      </div>
    </React.Fragment>
  )
}

export default BusinessManagement
